import React from "react";
import { Check } from "lucide-react";

// Derives the goods-delivery progress stages from a ride document.
export function goodsSteps(ride, opts = {}) {
  const s = ride?.status;
  const cancelled = s === "cancelled";
  const assigned = ["driver_assigned", "driver_arriving", "in_progress", "payment_pending", "completed"].includes(s);
  const arrived = !!ride?.arrived_at || ["in_progress", "payment_pending", "completed"].includes(s);
  const pickedUp = ["in_progress", "payment_pending", "completed"].includes(s);
  const hasProof = !!(ride?.proof_photo_id || ride?.proof_signature_id);
  const dropVerified = !!ride?.drop_code_verified || s === "completed";
  const completed = s === "completed";
  const online = ride?.payment_method === "razorpay";
  const paymentDone = online ? ride?.payment_status === "paid" : true;
  const steps = [
    { key: "placed", label: "Order placed", done: true },
    opts.receiver
      ? { key: "payment", label: "Order confirmed", done: true }
      : { key: "payment", label: online ? "Payment received" : "Pay at pickup selected", done: paymentDone },
    { key: "assigned", label: "Delivery partner assigned", done: assigned },
    { key: "arrived", label: "Partner at pickup", done: arrived },
    { key: "pickup", label: "Pickup code verified", done: pickedUp },
    { key: "otw", label: "On the way to drop", done: pickedUp },
    { key: "photo", label: "Delivery photo uploaded", done: hasProof },
    { key: "dropotp", label: "Delivery code verified", done: dropVerified },
    { key: "done", label: "Delivery complete", done: completed },
  ];
  const firstPending = steps.findIndex((x) => !x.done);
  return { steps, currentIdx: firstPending === -1 ? steps.length - 1 : firstPending, cancelled };
}

export function GoodsProgress({ ride, className = "", receiver = false, full = false }) {
  const { steps, currentIdx, cancelled } = goodsSteps(ride, { receiver });
  if (cancelled) {
    return (
      <div className={`rounded-xl bg-red-50 px-3 py-2 text-center text-sm font-bold text-red-600 ${className}`} data-testid="goods-progress-cancelled">
        Delivery cancelled
      </div>
    );
  }
  if (full) {
    return (
      <div className={`rounded-xl bg-white/95 p-3 ${className}`} data-testid="goods-progress">
        <ol className="space-y-0">
          {steps.map((st, i) => {
            const isCurrent = i === currentIdx;
            const last = i === steps.length - 1;
            return (
              <li key={st.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  {st.done ? (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-white" data-testid={`goods-progress-step-${st.key}-done`}><Check className="h-3.5 w-3.5" /></span>
                  ) : isCurrent ? (
                    <span className="relative flex h-6 w-6 shrink-0 items-center justify-center" data-testid={`goods-progress-step-${st.key}-current`}>
                      <span className="absolute inline-flex h-6 w-6 animate-ping rounded-full bg-brand/30" />
                      <span className="relative h-3.5 w-3.5 rounded-full bg-brand" />
                    </span>
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center" data-testid={`goods-progress-step-${st.key}`}><span className="h-3 w-3 rounded-full border-2 border-surface-300 bg-white" /></span>
                  )}
                  {!last && <span className={`my-0.5 w-0.5 flex-1 ${st.done ? "bg-brand" : "bg-surface-200"}`} style={{ minHeight: 14 }} />}
                </div>
                <p className={`pb-3 text-sm ${isCurrent ? "font-bold text-ink" : st.done ? "font-medium text-ink-soft" : "text-ink-muted"}`} data-testid={`goods-progress-label-${st.key}`}>
                  {st.label}{isCurrent ? <span className="ml-1.5 inline-block rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">Now</span> : null}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }
  return (
    <div className={`rounded-xl bg-white/95 px-3 py-2 shadow backdrop-blur ${className}`} data-testid="goods-progress">
      <div className="flex items-center">
        {steps.map((st, i) => {
          const isCurrent = i === currentIdx;
          return (
            <React.Fragment key={st.key}>
              {st.done ? (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-white" data-testid={`goods-progress-step-${st.key}-done`}><Check className="h-3 w-3" /></span>
              ) : isCurrent ? (
                <span className="relative flex h-5 w-5 shrink-0 items-center justify-center" data-testid={`goods-progress-step-${st.key}-current`}>
                  <span className="absolute inline-flex h-5 w-5 animate-ping rounded-full bg-brand/30" />
                  <span className="relative h-3 w-3 rounded-full bg-brand" />
                </span>
              ) : (
                <span className="h-3 w-3 shrink-0 rounded-full bg-surface-200" data-testid={`goods-progress-step-${st.key}`} />
              )}
              {i < steps.length - 1 && <span className={`mx-1 h-0.5 flex-1 ${steps[i + 1].done || i < currentIdx ? "bg-brand" : "bg-surface-200"}`} />}
            </React.Fragment>
          );
        })}
      </div>
      <p className="mt-1.5 text-center text-sm font-bold text-ink" data-testid="goods-progress-label">
        Step {currentIdx + 1} of {steps.length} · {steps[currentIdx].label}
      </p>
    </div>
  );
}

export default GoodsProgress;
